/**
 * Policy Comparison — the expiring policy against the proposed one.
 *
 * The thing an agent is actually trying to find here is not "what changed" in
 * the abstract. It is the line that quietly did not make it onto the renewal.
 * So dropped lines sort to the top, get their own colour, and get counted in
 * the summary before anything else is shown.
 *
 * ⚠️ IT NEVER SAYS ONE SIDE IS BETTER, AND THAT SURVIVED THE LIMITS BEING
 * PARSED. Since 2026-09-07 the extraction attempts occurrence limit, aggregate
 * and deductible as numbers, so a figure moving is reported as a figure moving
 * — "$2M → $1M" is arithmetic. What it still will not say is "reduced" or
 * "worse": sublimits, endorsements and form numbers are not extracted at all,
 * so a lower number is not the same claim as narrower cover, and this is a tool
 * an agent quotes to a client.
 *
 * ⚠️ THE VERBATIM STRING STAYS THE PRIMARY THING ON SCREEN. It was copied off
 * the dec page; the figures beside it were interpreted from it, and only one of
 * those two can be wrong. The chips are deliberately smaller and quieter than
 * the line they came from. See apps/api/src/inspector/compare.ts.
 *
 * ⚠️ AND IT NEVER PRINTS A TOTAL WITHOUT SAYING WHAT IS MISSING FROM IT. A
 * premium the extraction did not find comes back as zero, so a total silently
 * understates. Where any policy on a side had no premium, the number is shown
 * with the count beside it rather than on its own.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, type Comparison, type ComparisonCell, type LineVerdict } from '../lib/api';

const money = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const VERDICT: Record<LineVerdict, { label: string; tone: string }> = {
  missing: { label: 'Not on both', tone: 'is-gone' },
  'carrier-changed': { label: 'Carrier changed', tone: 'is-moved' },
  /*
    ⚠️ ITS OWN TONE, AND THE LOUDER ONE. A limit that moved is not the same class
    of finding as a price that moved, and giving them the same badge would make
    the column stop being worth reading.
  */
  'limits-changed': { label: 'Limits moved', tone: 'is-gone' },
  'premium-changed': { label: 'Premium changed', tone: 'is-moved' },
  'limits-differ': { label: 'Limits worded differently', tone: 'is-moved' },
  unchanged: { label: 'No change found', tone: 'is-same' },
};

/** A parsed figure, short — $1M rather than $1,000,000 in a narrow cell. */
const short = (n: number): string =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`
    : n >= 1_000
      ? `$${Math.round(n / 1_000)}k`
      : `$${n}`;

/** One side's cell for one line. Null renders as the absence, not as blank. */
function Cell({ cell, missingLabel }: { cell: ComparisonCell | null; missingLabel: string }): JSX.Element {
  if (!cell) {
    return (
      <td className="pc-absent">
        <b>Not on {missingLabel}</b>
      </td>
    );
  }
  return (
    <td>
      <b>{cell.carrier || '—'}</b>
      {cell.policyNumber && <div className="pc-sub">{cell.policyNumber}</div>}
      {/* Verbatim from the policy — always shown, because copying cannot be
          wrong in the way parsing can. */}
      <div className="pc-limits">{cell.limits || 'No limits read'}</div>
      {/* The parsed figures, only where they exist. Chips rather than prose, so
          the eye can compare two cells across without reading either. */}
      {(cell.occurrenceLimit || cell.aggregateLimit || cell.deductibleAmount || cell.deductible) && (
        <div className="pc-figs">
          {cell.occurrenceLimit && <span title="Per occurrence">occ {short(cell.occurrenceLimit)}</span>}
          {cell.aggregateLimit && <span title="Aggregate">agg {short(cell.aggregateLimit)}</span>}
          {(cell.deductibleAmount || cell.deductible) && (
            <span title="Deductible">
              ded {cell.deductibleAmount ? short(cell.deductibleAmount) : cell.deductible}
            </span>
          )}
        </div>
      )}
      <div className="pc-prem">
        {cell.premiumMissing ? <span className="pc-nopremium">No premium read</span> : money(cell.premium)}
      </div>
    </td>
  );
}

export function PolicyComparison({ accountId, ready }: { accountId: string; ready: boolean }): JSX.Element | null {
  const [cmp, setCmp] = useState<Comparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!ready) return;
    setLoading(true);
    api
      .inspectorComparison(accountId)
      .then((r) => {
        setCmp(r.comparison);
        setErr('');
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not build the comparison.'))
      .finally(() => setLoading(false));
    // `ready` flips when a side finishes extracting, which is exactly when this
    // needs rebuilding — the account object itself changes for many other reasons.
  }, [accountId, ready]);

  /*
    ⚠️ NOTHING AT ALL UNTIL THERE ARE TWO SIDES TO COMPARE. An empty comparison
    panel on every single-policy account would train people to scroll past it,
    and this is the panel that has to be noticed on the one account where a line
    went missing.
  */
  if (!ready) return null;
  if (loading) return null;
  if (err) {
    return (
      <section className="pu-panel">
        <div className="pu-head">
          <div>
            <h3>Policy comparison</h3>
            <p className="is-warn">{err}</p>
          </div>
        </div>
      </section>
    );
  }
  if (!cmp) return null;

  const labels = cmp.sides.map((s) => s.label);

  return (
    <section className="pu-panel">
      <div className="pu-head">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5h6v14H4zM14 5h6v14h-6zM10 12h4" />
        </svg>
        <div>
          <h3>Policy comparison</h3>
          <p>
            {labels.join(' against ')} — line by line, from what was read on each side.
          </p>
        </div>
      </div>

      {/*
        The headline, and the order is the argument: what is missing, then what
        moved, then what did not. A dropped line is the finding this whole panel
        exists to surface.
      */}
      <div className="pc-summary">
        <div className={cmp.counts.missing ? 'pc-stat is-gone' : 'pc-stat'}>
          <b>{cmp.counts.missing}</b>
          <span>{cmp.counts.missing === 1 ? 'line on one side only' : 'lines on one side only'}</span>
        </div>
        <div className="pc-stat">
          <b>{cmp.counts.changed}</b>
          <span>changed</span>
        </div>
        <div className="pc-stat">
          <b>{cmp.counts.unchanged}</b>
          <span>no change found</span>
        </div>
        {cmp.sides.map((s) => (
          <div className="pc-stat pc-total" key={s.id}>
            <b>{money(s.totalPremium)}</b>
            <span>
              {s.label}
              {/* The total is wrong by an unknown amount when this is non-zero, so say it. */}
              {s.premiumMissing > 0 && (
                <em>
                  {' '}
                  — {s.premiumMissing} of {s.policyCount} with no premium read
                </em>
              )}
            </span>
          </div>
        ))}
      </div>

      {cmp.notExtracted.length > 0 && (
        <p className="supp-note">
          Not included: {cmp.notExtracted.join(', ')} — nothing has been read on{' '}
          {cmp.notExtracted.length === 1 ? 'that side' : 'those sides'} yet.
        </p>
      )}

      <div className="pc-scroll">
        <table className="pc-table">
          <thead>
            <tr>
              <th>Line</th>
              {cmp.sides.map((s) => (
                <th key={s.id}>
                  {s.label}
                  {s.isPrimary && <span className="pc-flag">main</span>}
                </th>
              ))}
              <th>What changed</th>
            </tr>
          </thead>
          <tbody>
            {cmp.lines.map((l) => (
              <tr key={l.key} className={l.verdict === 'missing' ? 'is-gone-row' : ''}>
                <th scope="row">{l.name}</th>
                {l.cells.map((c, i) => (
                  <Cell key={cmp.sides[i]!.id} cell={c} missingLabel={labels[i] ?? 'this side'} />
                ))}
                <td>
                  <span className={`tag ${VERDICT[l.verdict].tone}`}>{VERDICT[l.verdict].label}</span>
                  {l.premiumDelta !== null && l.premiumDelta !== 0 && (
                    <div className={l.premiumDelta > 0 ? 'pc-delta is-up' : 'pc-delta is-down'}>
                      {l.premiumDelta > 0 ? '+' : '−'}
                      {money(Math.abs(l.premiumDelta))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cmp.schedules.length > 0 && (
        <>
          <h4 className="pc-h4">Schedules</h4>
          {/*
            ⚠️ COUNTS, NOT NAMES. "Six vehicles became five" is checkable.
            Naming which vehicle came off would mean matching VINs across two AI
            readings of two different documents, and a mismatch would accuse an
            agent of dropping a unit that is still on the policy.
          */}
          <div className="pc-scroll">
            <table className="pc-table pc-sched">
              <thead>
                <tr>
                  <th>Schedule</th>
                  {cmp.sides.map((s) => (
                    <th key={s.id}>{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cmp.schedules.map((s) => (
                  <tr key={s.label} className={s.differs ? 'is-gone-row' : ''}>
                    <th scope="row">{s.label}</th>
                    {s.counts.map((n, i) => (
                      <td key={cmp.sides[i]!.id}>{n === null ? <span className="pc-sub">not read</span> : n}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/*
        ⚠️ NOT BOILERPLATE, AND NOT MOVEABLE TO THE BOTTOM OF A LONG PAGE. This
        table looks authoritative — it is a grid of policy numbers and dollar
        amounts — and the one thing it cannot do is tell you whether coverage got
        worse. Somebody will otherwise show it to a client as though it had.
      */}
      <p className="pc-caveat">
        <b>This compares what was read, not whether coverage is adequate.</b> Limits are shown
        exactly as they appear on each policy; where a figure could be read out of them it is
        compared as a number, and a number is only ever called changed when both sides have one.
        Nothing here judges one side as broader than the other — sublimits, endorsements and form
        numbers are not extracted at all. Read the forms before advising anyone.
      </p>
    </section>
  );
}
